/* eslint-disable @harmony/stencil/no-import-css */
import React, { useState, useContext, useRef, useEffect } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import PropTypes from 'prop-types';
import {
  Input,
  FormController,
  FormLabel,
  FormHelpMessage,
  InputAdornment
} from '@one-thd/sui-atomic-components';
import { formatCardNumber, isNumber, removeSpaces, validateCardNumber } from '../../utils/utils';
import { CARD_NUMBER_FORMAT, CHECK_BALANCE_RESTRICTED_PROGRAM } from '../../utils/consts';
import BarcodeScanner from '../../utils/scanning/BarcodeScanningHelper';
import '../../../styles/barcode-scanner.css';

const barcodeScanner = new BarcodeScanner();

const defaultStatus = {
  state: null,
  message: 'Tap the barcode icon to scan gift card.'
};

const defaultCardInput = {
  cardNumber: '',
  cursorStart: 0,
  cursorEnd: 0
};

const CardNumberInput = ({
  disabled,
  onReady,
  restrictedProgramCodes,
  onInitScan,
  onScanSuccess
}) => {
  const [cardInput, setCardInput] = useState(defaultCardInput);
  const [status, setStatus] = useState(defaultStatus);
  const inputRef = useRef(null);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.setSelectionRange(cardInput?.cursorEnd + 1, cardInput?.cursorEnd + 1);
    }
  }, [cardInput?.cursorEnd]);

  const handleChange = (event) => {
    const { value, selectionStart } = event?.target;
    const _val = removeSpaces(value);

    if (value && !isNumber(_val)) {
      return;
    }
    let isBackspace = _val.length < removeSpaces(cardInput?.cardNumber).length;
    const { formatted, nextCusPos } = formatCardNumber(_val, selectionStart, isBackspace);
    setCardInput({
      cardNumber: formatted,
      cursorStart: selectionStart,
      cursorEnd: nextCusPos
    });
  };

  const handleOnBlur = (event) => {
    const { value } = event?.target;
    if (!value) {
      setStatus(defaultStatus);
      onReady('');
      return;
    }
    const _val = removeSpaces(value);

    const errorMessage = validateCardNumber(_val, restrictedProgramCodes);
    if (errorMessage) {
      setStatus({
        state: 'error',
        message: errorMessage
      });
      onReady('');
    } else {
      setStatus(defaultStatus);
      onReady(_val);
    }
  };

  const handleOpenScanner = () => {
    if (onInitScan) {
      onInitScan();
    }
    barcodeScanner.startScanning().then((number) => {
      if (number) {
        const errorMessage = validateCardNumber(number, restrictedProgramCodes);
        if (errorMessage) {
          setStatus({
            state: 'error',
            message: errorMessage
          });
        } else {
          const { formatted, nextCusPos } = formatCardNumber(number, 0, false);
          setCardInput({
            cardNumber: formatted,
            cursorStart: 0,
            cursorEnd: nextCusPos
          });
          setStatus({
            state: 'success',
            message: 'Scan successful'
          });
          if (onScanSuccess) {
            onScanSuccess();
          }
          onReady(number);
        }
      }
    });
  };

  const handleOnFocus = (_event) => {
    if (isMobile && status.state && status.state === 'error') {
      setStatus(defaultStatus);
    }
  };

  const renderHelpMessage = () => {
    if (!status.state && !isMobile) {
      return null;
    }

    let textClassName = status?.state === 'success' ? 'sui-text-success' : 'sui-text-subtle';
    textClassName = status?.state === 'error' ? 'sui-text-danger' : 'sui-text-subtle';

    return (
      <FormHelpMessage
        id="input-card-number-message"
        className={textClassName}
        data-testid="input-card-number-message"
      >
        {status.message}
      </FormHelpMessage>
    );
  };

  return (
    <FormController fullWidth>
      <FormLabel required htmlFor="card-number-input">
        Gift Card Number
      </FormLabel>
      <Input
        disabled={disabled}
        value={cardInput?.cardNumber}
        aria-labelledby="input-card-number"
        data-testid="input-card-number"
        placeholder={CARD_NUMBER_FORMAT.replace(/x/g, '0')}
        status={status.state}
        inputRef={inputRef}
        inputAttributes={{
          maxLength: CARD_NUMBER_FORMAT.length
        }}
        onBlur={handleOnBlur}
        onChange={handleChange}
        onFocus={handleOnFocus}
        endAdornment={
          isMobile && (
            <InputAdornment position="end">
              <button
                aria-label="Button for Searching by Scanning a Barcode with Your Phone"
                onClick={handleOpenScanner}
                data-testid="scanner-button"
                type="button"
              >
                <img
                  width={24}
                  height={24}
                  src="https://assets.homedepot-static.com/images/v1/barcode2.svg"
                  alt="scan barcode"
                />
              </button>
            </InputAdornment>
          )
        }
      />
      {renderHelpMessage()}
    </FormController>
  );
};

CardNumberInput.propTypes = {
  disabled: PropTypes.bool,
  onReady: PropTypes.func,
  onInitScan: PropTypes.func,
  onScanSuccess: PropTypes.func,
  restrictedProgramCodes: PropTypes.arrayOf(PropTypes.string)
};

CardNumberInput.defaultProps = {
  disabled: false,
  onReady: () => {},
  onInitScan: () => {},
  onScanSuccess: () => {},
  restrictedProgramCodes: CHECK_BALANCE_RESTRICTED_PROGRAM
};

export default CardNumberInput;
